import React from "react";
import { Navigate } from "react-router-dom";
import { ComponentLoader } from "@snsw-gel/react";
import { useSageAccess, isAthenaOnly, PRODUCT_ATHENA, type SageProduct } from "./access";
import { NoAccess } from "./NoAccess";

// Where each product's front door lives. /home is Bonds today; a future
// Sage-branded home may take it over (see ADR 0005 follow-up note).
export const BONDS_HOME = "/home";
export const ATHENA_HOME = "/athena/search";
export const ATHENA_CHAT = "/athena/chat";

const homeFor = (product: SageProduct): string => (product === PRODUCT_ATHENA ? ATHENA_HOME : BONDS_HOME);

interface RequireProductProps {
  product: SageProduct;
  children: React.ReactNode;
}

/**
 * RequireProduct — in-app product gate, the merged-app successor to the
 * cross-app AthenaGate hand-off. UX only; the Athena proxy fail-closes its
 * own data routes regardless of what this guard decides.
 *
 * Athena routes (fail closed):
 *   - feature disabled → 404 (handled by the route never rendering this)
 *   - pending          → full-page loader
 *   - granted          → children
 *   - denied + bonds   → redirect to the Bonds home
 *   - denied/failed    → NoAccess page
 *
 * Bonds routes (fail open): the only way out of Bonds is an explicit
 * athena-without-bonds grant, which redirects to Athena's front door —
 * this reproduces the old AthenaGate behaviour as a router navigation.
 */
export const RequireProduct: React.FC<RequireProductProps> = ({ product, children }) => {
  const access = useSageAccess();

  if (product === PRODUCT_ATHENA) {
    if (access.status === "disabled") {
      return <NoAccess status="disabled" />;
    }
    if (access.status === "pending") {
      return <ComponentLoader active fullPage label="One moment, please..." />;
    }
    if (access.status === "failed") {
      return <NoAccess status="failed" />;
    }
    if (access.hasAthena) {
      return <>{children}</>;
    }
    if (access.hasBonds) {
      return <Navigate to={BONDS_HOME} replace />;
    }
    return <NoAccess status="denied" />;
  }

  // product === "bonds"
  if (access.status === "pending") {
    return null; // hold, so an athena-only user never flashes Bonds
  }
  if (isAthenaOnly(access)) {
    return <Navigate to={homeFor(PRODUCT_ATHENA)} replace />;
  }
  return <>{children}</>;
};
